import { usePeopleManagementApi } from 'infrastructure/api';
import EMPLOYEE_CONTRACT_VALIDATION from 'domain/people/employee/contracts/createupdate/employeeContractValidation';
import { useBaseCrudService } from 'framework/crud';
import { ASC } from 'framework/list';
import { useEmployeeId } from 'domain/people/employee/employeeIdentityRouting';
import { EMPLOYEE_CONTRACT_TYPES_ALIASES, isPolishOffice } from 'domain/shared/employee';
import { FormFieldsData } from 'framework/form';

export const CREATE_EMPLOYEE_CONTRACT = 'PEOPLE_MANAGEMENT_CONTRACT_CREATE';
export const READ_EMPLOYEE_CONTRACTS_LIST = 'PEOPLE_MANAGEMENT_CONTRACT_LIST_SEARCH_FOR_EMPLOYEE';
export const READ_EMPLOYEE_CONTRACT = 'PEOPLE_MANAGEMENT_CONTRACT_GET_CONTRACT';
export const UPDATE_EMPLOYEE_CONTRACT = 'PEOPLE_MANAGEMENT_CONTRACT_UPDATE';
export const DELETE_EMPLOYEE_CONTRACT = 'PEOPLE_MANAGEMENT_CONTRACT_DELETE';
export const SET_PROBATION_END_DATE = 'PEOPLE_MANAGEMENT_CONTRACT_PARTIAL_UPDATE';

export const MIN_PAID_LEAVE_BALANCE = 20;
export const NON_STRICT_RULES = ['CONTRACT-BR-1'];

export const PAID_LEAVE_BALANCES = {
  [MIN_PAID_LEAVE_BALANCE]: '20 working days',
  26: '26 working days',
};
export const isEmployeeContract = (formData: FormFieldsData) => {
  return EMPLOYEE_CONTRACT_TYPES_ALIASES.EC.toString() === formData.contractType.toString();
};

const useEmployeeContractsService = () => {
  const { sendGetRequest, sendPostRequest } = usePeopleManagementApi();
  const currentAlias = useEmployeeId();

  const baseCrudOperations = useBaseCrudService({
    singleResourceUrl: '/contracts/:id',
    listResourceUrl: '/contracts',
    useService: usePeopleManagementApi,
  });

  const search = async ({
    pageNumber,
    sort = {
      dateOfSignature: ASC,
    },
    employeeAlias = currentAlias,
    pageSize,
  }: any) => {
    const [sortBy, sortOrder] = Object.entries(sort)[0];
    const params = [`pageNumber=${pageNumber}&pageSize=${pageSize}`];
    params.push(`sort[${sortBy}]=${sortOrder}`);

    const url = `/employees/${employeeAlias}/contracts?${params.join('&')}`;
    const response = await sendGetRequest(encodeURI(url));
    const data = await response.json();
    return data;
  };

  const getAll = async (employeeAlias: any) => {
    const url = `/employees/${employeeAlias}/contracts`;
    const response = await sendGetRequest(url);
    const contracts = await response.json();

    return contracts;
  };

  const mapContract = (data: any) => {
    const {
      number,
      paidLeaveBalance,
      contractPosition,
      contractType,
      isIndefinite,
      scanIds,
      hasProbationPeriod,
      ...rest
    } = data;
    const ignoreNotStrictRules = data.ignoreNotStrictRules ? data.ignoreNotStrictRules : data.nonStrictRulesIgnored;
    const isNoProbationType = data.contractType === EMPLOYEE_CONTRACT_TYPES_ALIASES.INTERNSHIP;
    return {
      ...rest,
      ...(!isPolishOffice(String(data.officeId)) && { number }),
      position: contractPosition,
      type: contractType,
      indefinite: isIndefinite,
      hasProbationPeriod: isNoProbationType ? false : data.hasProbationPeriod,
      scansIds: scanIds,
      id: undefined,
      ignoreNotStrictRules,
      paidLeaveBalance: paidLeaveBalance === null ? 0 : paidLeaveBalance,
    };
  };

  const create = async (data: FormFieldsData, employeeAlias = currentAlias) => {
    const requestData = mapContract(data);

    const response = await sendPostRequest(`/employees/${employeeAlias}/contracts`, requestData);
    const result = await response.json();
    return result;
  };

  const { update: baseUpdate } = baseCrudOperations;

  const update = async (id: StringOrNumber, data: FormFieldsData) => {
    return baseUpdate(id, mapContract(data));
  };

  const getValidationSchema = () => Promise.resolve(EMPLOYEE_CONTRACT_VALIDATION);

  return {
    ...baseCrudOperations,
    search,
    create,
    update,
    getValidationSchema,
    getAll,
  };
};

export default useEmployeeContractsService;
